/**
 * MODULE: domain/pages/frontend/utils/blockTransformUtils.ts
 * PURPOSE: Notion-style "Turn into" block transforms for the Pages note editor
 * PUBLIC_API: getAvailableBlockTransforms, applyBlockTransform, supportsBlockTransform
 * SIDE_EFFECTS: TipTap document mutations when applyBlockTransform runs
 * AGENT SAFETY: 🟢 SAFE
 */

import type { Editor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import type { DraggableBlockTarget } from './pagesBlockDragTarget';

export type BlockTransformId =
  | 'paragraph'
  | 'heading_1'
  | 'heading_2'
  | 'heading_3'
  | 'bulleted_list'
  | 'numbered_list'
  | 'quote'
  | 'code'
  | 'todo';

export type BlockTransformOption = {
  id: BlockTransformId;
  label: string;
};

const TRANSFORM_OPTIONS: BlockTransformOption[] = [
  { id: 'paragraph', label: 'Text' },
  { id: 'heading_1', label: 'Heading 1' },
  { id: 'heading_2', label: 'Heading 2' },
  { id: 'heading_3', label: 'Heading 3' },
  { id: 'bulleted_list', label: 'Bulleted list' },
  { id: 'numbered_list', label: 'Numbered list' },
  { id: 'todo', label: 'To-do' },
  { id: 'quote', label: 'Quote' },
  { id: 'code', label: 'Code' },
];

const CUSTOM_TEXT_BLOCKS = new Set(['pagesToggleBlock', 'pagesCalloutBlock']);

export function resolveBlockTransformKind(node: ProseMirrorNode): BlockTransformId | null {
  switch (node.type.name) {
    case 'paragraph':
      return 'paragraph';
    case 'heading': {
      const level = Number(node.attrs.level ?? 1);
      if (level === 2) return 'heading_2';
      if (level === 3) return 'heading_3';
      return 'heading_1';
    }
    case 'bulletList':
      return 'bulleted_list';
    case 'orderedList':
      return 'numbered_list';
    case 'blockquote':
      return 'quote';
    case 'codeBlock':
      return 'code';
    case 'pagesTodoBlock':
      return 'todo';
    default:
      return null;
  }
}

export function supportsBlockTransform(node: ProseMirrorNode, isNested: boolean): boolean {
  if (resolveBlockTransformKind(node) != null) {
    return true;
  }
  if (isNested && node.type.name === 'paragraph') {
    return true;
  }
  return CUSTOM_TEXT_BLOCKS.has(node.type.name);
}

export function getAvailableBlockTransforms(
  node: ProseMirrorNode,
  isNested: boolean
): BlockTransformOption[] {
  if (!supportsBlockTransform(node, isNested)) {
    return [];
  }

  const current = resolveBlockTransformKind(node);
  return TRANSFORM_OPTIONS.filter((option) => {
    if (option.id === current) {
      return false;
    }
    if (
      isNested &&
      (option.id === 'bulleted_list' ||
        option.id === 'numbered_list' ||
        option.id === 'todo' ||
        option.id === 'quote')
    ) {
      return false;
    }
    return true;
  });
}

function extractPlainText(node: ProseMirrorNode): string {
  let text = '';
  node.descendants((child) => {
    if (child.isText) {
      text += child.text;
    }
  });
  return text;
}

function extractBlockText(node: ProseMirrorNode): string {
  if (node.type.name === 'pagesToggleBlock') {
    const title = String(node.attrs.title ?? '').trim();
    const body = String(node.attrs.body ?? '').trim();
    return [title, body].filter(Boolean).join('\n');
  }
  if (node.type.name === 'pagesCalloutBlock') {
    return String(node.attrs.text ?? '').trim();
  }
  if (node.type.name === 'pagesTodoBlock') {
    return String(node.attrs.text ?? '').trim();
  }
  if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
    const lines: string[] = [];
    node.forEach((child) => {
      if (child.type.name === 'listItem') {
        const line = extractPlainText(child).trim();
        if (line) lines.push(line);
      }
    });
    return lines.join('\n');
  }
  if (node.type.name === 'blockquote') {
    return extractPlainText(node).trim();
  }
  return extractPlainText(node).trim();
}

function textNode(schema: ProseMirrorNode['type']['schema'], text: string) {
  return text ? schema.text(text) : undefined;
}

function buildReplacementNode(
  schema: ProseMirrorNode['type']['schema'],
  transformId: BlockTransformId,
  text: string
): ProseMirrorNode | null {
  const inline = textNode(schema, text);

  switch (transformId) {
    case 'paragraph':
      return schema.nodes.paragraph!.create(null, inline);
    case 'heading_1':
      return schema.nodes.heading!.create({ level: 1 }, inline);
    case 'heading_2':
      return schema.nodes.heading!.create({ level: 2 }, inline);
    case 'heading_3':
      return schema.nodes.heading!.create({ level: 3 }, inline);
    case 'code':
      return schema.nodes.codeBlock!.create(null, inline);
    case 'quote':
      return schema.nodes.blockquote!.create(null, [
        schema.nodes.paragraph!.create(null, inline),
      ]);
    case 'bulleted_list':
      return schema.nodes.bulletList!.create(null, [
        schema.nodes.listItem!.create(null, [schema.nodes.paragraph!.create(null, inline)]),
      ]);
    case 'numbered_list':
      return schema.nodes.orderedList!.create(null, [
        schema.nodes.listItem!.create(null, [schema.nodes.paragraph!.create(null, inline)]),
      ]);
    case 'todo':
      if (!schema.nodes.pagesTodoBlock) {
        return null;
      }
      return schema.nodes.pagesTodoBlock.create({ text, checked: false });
    default:
      return null;
  }
}

/** Apply a transform to the draggable block at target.pos (preserves plain text where possible). */
export function applyBlockTransform(
  editor: Editor,
  target: DraggableBlockTarget,
  transformId: BlockTransformId,
  isNested: boolean
): boolean {
  if (!editor.isEditable) {
    return false;
  }

  const { state, view } = editor;
  const text = extractBlockText(target.node);
  const replacement = buildReplacementNode(state.schema, transformId, text);
  if (!replacement) {
    return false;
  }

  const from = target.pos;
  const to = target.pos + target.node.nodeSize;
  const { tr } = state;
  tr.replaceWith(from, to, replacement);
  tr.setSelection(NodeSelection.create(tr.doc, from));
  view.dispatch(tr);
  editor.commands.focus();

  // Suppress unused — nested flag gates menu options; replacement path is identical.
  void isNested;

  return true;
}

export function blockTransformLabel(id: BlockTransformId): string {
  return TRANSFORM_OPTIONS.find((option) => option.id === id)?.label ?? id;
}
